/**
 * uscfSearch.ts - "Search for your USCF ID" modal: debounced search,
 * results list, and binding a selected result back to the triggering
 * player card.
 *
 * The API returns every rating type per player (see UscfPlayerRating);
 * this module resolves whichever one matches the event's eligibility
 * rating type before displaying or storing it.
 */
import { CONFIG } from './config';
import { getEventContext } from './utils';
import { renderUscfStatusSuccess } from './uscfValidationStatus';
import type { UscfPlayerRating, UscfSearchResult } from './types';

let activePlayerCardForSearch: HTMLElement | null = null;
let searchDebounceTimer: number | null = null;

export function initUscfSearchModal(): void {
    const modalElement = document.getElementById('UscfSearchModal');
    const searchInput = document.getElementById('UscfSearchQuery') as HTMLInputElement | null;
    const resultsContainer = document.getElementById('UscfSearchResults');

    if (!modalElement || !searchInput || !resultsContainer) return;

    // Open modal and bind to specific player card
    document.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;
        const trigger = target.closest('.js-btn-open-uscf-search');
        if (trigger) {
            e.preventDefault();
            activePlayerCardForSearch = trigger.closest('.player-card') as HTMLElement;

            // Pre-fill search query if First/Last name were already typed in the form
            const firstName = activePlayerCardForSearch?.querySelector<HTMLInputElement>('.js-first-name')?.value.trim() || '';
            const lastName = activePlayerCardForSearch?.querySelector<HTMLInputElement>('.js-last-name')?.value.trim() || '';

            searchInput.value = `${firstName} ${lastName}`.trim();

            // Trigger Bootstrap modal
            const modal = new (window as any).bootstrap.Modal(modalElement);
            modal.show();

            if (searchInput.value.length >= 3) {
                performSearch(searchInput.value, resultsContainer, modal);
            }
        }
    });

    // Debounced input handler
    searchInput.addEventListener('input', () => {
        if (searchDebounceTimer) clearTimeout(searchDebounceTimer);

        searchDebounceTimer = window.setTimeout(() => {
            const query = searchInput.value.trim();
            if (query.length >= 3) {
                performSearch(query, resultsContainer, (window as any).bootstrap.Modal.getInstance(modalElement));
            } else {
                resultsContainer.innerHTML = '<div class="list-group-item text-muted text-center py-3">Type at least 3 letters to search...</div>';
            }
        }, 300);
    });
}

/**
 * Picks the rating matching this event's eligibility rating type out of
 * a player's full ratings list. Returns null if that type isn't present
 * (unrated for that type) or no event context is available.
 */
function resolveEligibleRating(ratings: UscfPlayerRating[]): number | null {
    const { eligibilityRatingType } = getEventContext();
    console.log('rating type', eligibilityRatingType);
    if (!eligibilityRatingType) return null;

    const typeNum = parseInt(eligibilityRatingType, 10);
    const match = ratings?.find(r => r.type === typeNum);
    return match?.rating ?? null;
}

async function performSearch(query: string, container: HTMLElement, modalInstance: any): Promise<void> {
    container.innerHTML = '<div class="list-group-item text-muted text-center py-3"><span class="spinner-border spinner-border-sm me-2"></span>Searching...</div>';

    try {
        const { eventDate } = getEventContext();

        const params = new URLSearchParams({ search: query });
        if (eventDate) params.set('eventDate', eventDate);

        const response = await fetch(`${CONFIG.uscfSearchApiUrl}?${params.toString()}`);
        const results: UscfSearchResult[] = await response.json();

        if (!results || results.length === 0) {
            container.innerHTML = '<div class="list-group-item text-muted text-center py-3">No matching players found.</div>';
            return;
        }

        container.innerHTML = '';
        results.forEach((player) => {
            const eligibleRating = resolveEligibleRating(player.ratings);

            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'list-group-item list-group-item-action d-flex justify-content-between align-items-center py-2';
            const titlePrefix = player.chessTitle ? `${player.chessTitle} ` : '';
            btn.innerHTML = `
                <div>
                    <strong class="d-block text-dark">${titlePrefix}${player.displayName}</strong>
                    <span class="small text-muted">ID: ${player.uscfId} | ${player.state} | Rating: ${eligibleRating ?? 'Unrated'}</span>
                </div>
                <span class="badge ${player.isActive ? 'bg-success-subtle text-success border border-success' : 'bg-warning-subtle text-warning border border-warning'} fw-normal">
                    ${player.isActive ? 'Active' : 'Expired ' + player.expirationDate}
                </span>
            `;

            btn.addEventListener('click', () => {
                if (activePlayerCardForSearch) {
                    const uscfInput = activePlayerCardForSearch.querySelector<HTMLInputElement>('.js-uscf-input');
                    const ratingInput = activePlayerCardForSearch.querySelector<HTMLInputElement>('.js-rating-input');
                    const firstNameInput = activePlayerCardForSearch.querySelector<HTMLInputElement>('.js-first-name');
                    const lastNameInput = activePlayerCardForSearch.querySelector<HTMLInputElement>('.js-last-name');

                    if (uscfInput) uscfInput.value = player.uscfId.toString();
                    if (ratingInput && eligibleRating != null) ratingInput.value = eligibleRating.toString();

                    // Prefill names from the search result (real fields
                    // now, not a whitespace split of a display name), but
                    // never clobber something the parent already typed.
                    if (firstNameInput && !firstNameInput.value.trim() && player.firstName) {
                        firstNameInput.value = player.firstName;
                    }
                    if (lastNameInput && !lastNameInput.value.trim() && player.lastName) {
                        lastNameInput.value = player.lastName;
                    }

                    // Render rating/membership status immediately - the
                    // search result already has everything we need, no
                    // extra lookup call required.
                    renderUscfStatusSuccess(activePlayerCardForSearch, {
                        rating: eligibleRating,
                        isActive: player.isActive,
                        expirationDate: player.expirationDate || null
                    });

                    // Automatically trigger eligibility re-evaluation
                    uscfInput?.dispatchEvent(new Event('change', { bubbles: true }));
                }

                if (modalInstance) modalInstance.hide();
            });

            container.appendChild(btn);
        });
    } catch (err) {
        console.error('USCF Search failed:', err);
        container.innerHTML = '<div class="list-group-item text-danger text-center py-3">Error executing search.</div>';
    }
}